import { useLocalStorageState } from 'ahooks';
import { Form, FormInstance } from 'antd';
import { isEmpty, isPlainObject } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppConfig } from '@/config/context';
import { kbToBytes, bytesToKb } from '@/infrastructure/format-data';
import { getProductId, isAndroid } from '@/infrastructure/product-info';
import { getCompareDateValue, searchValueDeserializer } from '@/infrastructure/utils/issue';
import { toArray } from '@/infrastructure/utils/to-array';
import {
  FormEntry,
  FormType, IFormRef,
  QueryForm,
} from '@/presentation/components/form';
import { NumberOperator } from '@/presentation/components/form/components/select-number';
import { DEFAULT_MONITOR_LIMIT_DAYS } from '@/presentation/components/form/components/time-range';
import { VersionSelectGroupName } from '@/presentation/components/form/components/version-select-group';
import { NUMBER_MATCH_OPTIONS } from '@/presentation/components/form/standards/types';
import { FilterEntry } from '@/presentation/components/form/types';
import {
  getVersionLabel,
  onChangeAppVersion,
  getShowSwitchStatus,
} from '@/presentation/components/form/utils';
import { ABTEST_TIPS, BUNDLE_TIPS_CASE, hintText } from '@/presentation/components/hint-text/hintText';
import { enumFilterTips, numberFilterTips } from '@/presentation/components/issue-list/components/tips';
import { SEARCH_VALUES } from '@/presentation/config/storage';
import { SearchValueStorageType } from '@/presentation/config/type';
import {
  AppVersionOperator,
  FIELD_ALL,
  FIELD_LABEL,
  FieldKey,
  Zone,
} from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { TimeType } from '@/proto/metric';
import { CompareFieldList, FieldList } from '@/proto/token';

interface IProps {
  onFinish: (value: Record<string, any>) => void;
  entry: FilterEntry;
  initialValue?: CompareFieldList & { timeRange: string[], isValueInit?: boolean };
  onValueInited?: (value: Record<string, any>) => void;
  form: FormInstance;
  tokenInitValues: FieldList & { isValueInit?: boolean };
}

/**
 * java内存泄漏-问题列表>查询表单
 */

export const ProblemForm: FC<IProps> = observer((props) => {
  const { tokenInitValues } = props;
  const [topForm] = Form.useForm();
  const tableRef = useRef<IFormRef>(null);
  const form = props.form ? props.form : topForm;
  const [showSwitch, setShowSwitch] = useState(false);
  const [searchValues, setSearchValues] = useLocalStorageState<SearchValueStorageType>(`${SEARCH_VALUES}-${props.entry}-${getProductId()}`, {
    defaultValue: [],
  });
  const config = useAppConfig();

  useEffect(() => {
    if (!isEmpty(tokenInitValues)) {
      const { isValueInit = true, appVersionGroup, appVersionOperator } = tokenInitValues;
      // 链接有token时，强制展示有value值的表单项
      tableRef?.current?.showFormItem?.(entries, tokenInitValues)?.then(() => {
        form.setFieldsValue(tokenInitValues);
        if (isValueInit) {
          props.onValueInited?.(form.getFieldsValue());
        }
        // 初始化显示 【只显示新增问题】字段
        setShowSwitch(getShowSwitchStatus({
          values: toArray(appVersionGroup ?? ''),
          operator: appVersionOperator ?? '',
        }));
      });
    }
  }, [tokenInitValues]);

  useEffect(() => {
    if (!isEmpty(props.initialValue)) {
      const { isValueInit = true, appVersionGroup, appVersionOperator } = props.initialValue;
      form.setFieldsValue(props.initialValue);
      if (isValueInit) {
        props.onValueInited?.(form.getFieldsValue());
      }
      // 初始化显示 【只显示新增问题】字段
      setShowSwitch(getShowSwitchStatus({
        values: toArray(appVersionGroup ?? ''),
        operator: appVersionOperator ?? '',
      }));
    }
  }, [props.initialValue]);

  const onFinish = (value: Record<string, any>, compareSwitch?: boolean) => {
    const { leakBytes, leakBytesCompare, ...rest } = value;
    const compareDateValue = getCompareDateValue(value, compareSwitch);
    if (compareSwitch) {
      form.setFieldsValue(compareDateValue);
    }
    if (leakBytes || leakBytesCompare) {
      props.onFinish({
        leakBytesMin: kbToBytes(leakBytes?.value[0]),
        leakBytesMax: kbToBytes(leakBytes?.value[1]),
        leakBytesMinCompare: kbToBytes(leakBytesCompare?.value[0]),
        leakBytesMaxCompare: kbToBytes(leakBytesCompare?.value[1]),
        ...rest,
        ...compareDateValue,
      });
    } else {
      props.onFinish({ ...value, ...compareDateValue });
    }
  };

  const entries: FormEntry[] = [
    {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      labelString: FIELD_LABEL[FieldKey.TimeRange],
      label: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'issues',
      deleteable: false,
      limitDays: DEFAULT_MONITOR_LIMIT_DAYS,
      showTime: props.entry === FilterEntry.MemoryJavaLeakDetail || props.entry === FilterEntry.MemoryJavaLeakList,
      initTimeWindow: TimeType.MINUTE5,
      hiddenTimeWindow: props.entry !== FilterEntry.MemoryJavaLeakList,
      isIssuesTimeWindow: props.entry === FilterEntry.MemoryJavaLeakList,
    },
    {
      type: FormType.VersionSelectGroup,
      leftName: FieldKey.AppVersionOperator,
      rightName: FieldKey.AppVersion,
      label: FIELD_LABEL[FieldKey.AppVersionOperator],
      leftFieldKey: FieldKey.AppVersionOperator,
      rightFieldKey: FieldKey.AppVersion,
      fieldScope: Zone.JavaLeak,
      scope: 'issues',
      mode: 'tags',
      leftInitialValue: AppVersionOperator.Or,
      rightInitialValue: [FIELD_ALL],
      name: VersionSelectGroupName.appVersionGroup,
      deleteable: false,
      hasMaxWidth: true,
    },
    {
      type: FormType.Select,
      name: 'appVersionMode',
      label: hintText(FIELD_LABEL[FieldKey.AppVersionMode], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.AppVersionMode],
      fieldKey: FieldKey.AppVersionMode,
      fieldScope: Zone.JavaLeak,
      mode: 'multiple',
      initialValue: [FIELD_ALL],
    },
    {
      type: FormType.Select,
      name: 'scene',
      label: hintText(FIELD_LABEL[FieldKey.Scene], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.Scene],
      fieldKey: FieldKey.Scene,
      fieldScope: Zone.JavaLeak,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 500,
    },
    {
      type: FormType.Input,
      name: 'brand',
      label: FIELD_LABEL[FieldKey.Brand],
      disabled: !isAndroid(),
    },
    {
      type: FormType.Select,
      name: 'processName',
      label: hintText(FIELD_LABEL[FieldKey.ProcessName], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.ProcessName],
      fieldKey: FieldKey.ProcessName,
      fieldScope: Zone.JavaLeak,
      mode: 'single',
      initialValue: FIELD_ALL,
    },
    {
      type: FormType.VersionSelect,
      name: FieldKey.OsVersion,
      versionName: FieldKey.OsVersion,
      labelString: FIELD_LABEL[FieldKey.OsVersion],
      label: getVersionLabel(FIELD_LABEL[FieldKey.OsVersion]),
      fieldKey: FieldKey.OsVersion,
      fieldScope: Zone.JavaLeak,
    },
    {
      type: FormType.Select,
      name: 'sdkVersion',
      label: hintText(FIELD_LABEL[FieldKey.SdkVersion], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.SdkVersion],
      fieldKey: FieldKey.SdkVersion,
      fieldScope: Zone.JavaLeak,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 300,
    },
    {
      type: FormType.AutoComplete,
      name: 'model',
      label: FIELD_LABEL[FieldKey.Model],
      fieldKey: FieldKey.Model,
      fieldScope: Zone.JavaLeak,
      initialValue: FIELD_ALL,
    },
    {
      type: FormType.Input,
      name: 'accountId',
      label: '用户ID',
    },
    {
      type: FormType.Input,
      name: 'qimei',
      label: '设备ID',
    },
    {
      type: FormType.Input,
      name: 'launchId',
      label: FIELD_LABEL[FieldKey.LaunchId],
      labelString: '启动ID',
    },
    {
      type: FormType.Input,
      name: 'processLaunchId',
      label: FIELD_LABEL[FieldKey.ProcessLaunchId],
      labelString: '进程启动ID',
    },
    {
      type: FormType.Input,
      name: 'clientIdentify',
      label: '消息ID',
    },
    {
      type: FormType.Input,
      name: 'feature',
      label: 'Issue ID',
      disabled: props.entry !== FilterEntry.MemoryJavaLeakList,
    },
    {
      type: FormType.Input,
      name: 'funcName',
      label: '问题特征',
      disabled: props.entry !== FilterEntry.MemoryJavaLeakList,
    },
    {
      type: FormType.SelectNumberGroup,
      name: 'leakBytes',
      label: hintText('最大泄漏内存（KB）', numberFilterTips),
      labelString: '最大泄漏内存（KB）',
      options: [{ value: NumberOperator.RANGE, label: '范围' }],
      processValue: (_result, allValues) => ({
        leakBytes: allValues?.leakBytes,
        leakBytesCompare: allValues?.leakBytesCompare,
      }),
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: NumberOperator.RANGE,
          value,
        };
      },
      initialValue: [
        bytesToKb(props.initialValue?.leakBytesMin),
        bytesToKb(props.initialValue?.leakBytesMax),
        bytesToKb(props.initialValue?.leakBytesMinCompare as number),
        bytesToKb(props.initialValue?.leakBytesMaxCompare as number),
      ],
      disabled: props.entry !== FilterEntry.MemoryJavaLeakList,
    },
    {
      type: FormType.Select,
      name: 'bundleId',
      label: hintText(FIELD_LABEL[FieldKey.BundleId], BUNDLE_TIPS_CASE),
      labelString: FIELD_LABEL[FieldKey.BundleId],
      fieldKey: FieldKey.BundleId,
      fieldScope: Zone.JavaLeak,
      mode: 'single',
      initialValue: FIELD_ALL,
    },
    {
      type: FormType.Select,
      name: FieldKey.ShiplyTag,
      label: hintText(FIELD_LABEL[FieldKey.ShiplyTag], ABTEST_TIPS),
      labelString: FIELD_LABEL[FieldKey.ShiplyTag],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.TestTag,
      fieldScope: Zone.JavaLeak,
      mode: 'tags',
      disabled: !config.enableShiplyAbTestTag,
    },
    {
      type: FormType.Select,
      name: FieldKey.AbTestTag,
      label: hintText(FIELD_LABEL[FieldKey.AbTestTag], ABTEST_TIPS),
      labelString: FIELD_LABEL[FieldKey.AbTestTag],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.AbTestTagLine,
      fieldScope: Zone.JavaLeak,
      mode: 'tags',
    },
    {
      type: FormType.Select,
      name: FieldKey.TagsInFilter,
      label: FIELD_LABEL[FieldKey.TagsInFilter],
      initialValue: [FIELD_ALL],
      fieldScope: Zone.JavaLeak,
      fieldKey: FieldKey.TagsInFilterUnderline,
      mode: 'tags',
      compareDisabled: true,
    },
    {
      type: FormType.Select,
      name: 'romName',
      label: hintText(FIELD_LABEL[FieldKey.Rom], ABTEST_TIPS),
      labelString: FIELD_LABEL[FieldKey.Rom],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.Rom,
      fieldScope: Zone.JavaLeak,
      mode: 'tags',
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.StandardLaunchDuration,
      label: hintText('使用时长（秒）', numberFilterTips),
      labelString: '使用时长（秒）',
      options: NUMBER_MATCH_OPTIONS,
    },
    {
      type: FormType.CustomFields,
      name: FieldKey.UserCustom,
      label: '自定义维度',
    },
    {
      type: FormType.CustomKeys,
      name: FieldKey.BizExtendInfo,
      label: '自定义字段',
    },
    {
      type: FormType.Select,
      name: 'showNew',
      label: '只显示新增问题',
      disabled: !showSwitch || props.entry !== FilterEntry.MemoryJavaLeakList,
      deleteable: false,
      mode: 'single',
      fieldKey: FieldKey.ShowNew,
      fieldScope: Zone.JavaLeak,
      initialValue: false,
      compareDisabled: true,
    },
  ];

  return <QueryForm
    form={form}
    ref={tableRef}
    entries={entries}
    onFinish={onFinish}
    onChange={async (__, fields) => onChangeAppVersion({
      values: toArray(fields.appVersionGroup ?? ''),
      operator: fields?.appVersionOperator ?? '',
    }, setShowSwitch)}
    onReset={() => setShowSwitch(false)}
    pageEntry={props.entry}
    issueType={IssueType.ISSUE_TYPE_MEM_LEAK}
    hasCompare={props.entry === FilterEntry.MemoryJavaLeakList}
    defaultValues={props.initialValue}
    isSaveSearchValues={true}
    searchValues={searchValueDeserializer(searchValues)}
    setSearchValues={setSearchValues}
  />;
});
